import React from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo192.png";

const features = [
  {
    icon: "🔎",
    title: "Real search engine",
    text: "Describe what you want in plain words, not just keyword matching.",
    color: "#efbf04",
  },
  {
    icon: "🎯",
    title: "Smart recommendations",
    text: "Similar-game suggestions that go beyond the same 4–5 titles.",
    color: "#9701fb",
  },
  {
    icon: "📋",
    title: "Your own top lists",
    text: "Log in and curate lists around your personal criteria.",
    color: "#a7c957",
  },
];

const steps = [
  { n: "01", title: "Describe or filter", text: "Search in free text or use advanced filters." },
  { n: "02", title: "Discover", text: "Open any game to get similar suggestions instantly." },
  { n: "03", title: "Save & organize", text: "Favourite games and build your own top lists." },
];

export default function AboutPage() {
  // Inject Poppins font if not already present
  React.useEffect(() => {
    const poppinsId = "poppins-font";
    if (!document.getElementById(poppinsId)) {
      const link = document.createElement("link");
      link.id = poppinsId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="min-h-[calc(100vh-var(--header-height))] py-8 sm:py-10 px-3 sm:px-6 md:px-8">
      <div className="max-w-[1000px] mx-auto">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-surface-card/40 backdrop-blur-md mb-6 px-6 sm:px-10 py-10 sm:py-14 text-center">
          <div className="absolute inset-0 gg-grid-backdrop opacity-40" />
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-56 w-[70%] rounded-full bg-brand-purple/25 blur-3xl animate-pulseGlow" />
          <div className="absolute top-6 right-10 h-24 w-24 rounded-full bg-brand-gold/15 blur-2xl animate-floaty" />
          <div className="relative animate-fadeUp">
            <span className="relative inline-flex items-center justify-center mb-5">
              <span className="absolute inset-0 rounded-full bg-brand-purple/40 blur-lg" />
              <img
                src={logo}
                alt="Game Goblin"
                className="relative h-16 w-16 sm:h-20 sm:w-20 object-contain drop-shadow-[0_2px_14px_rgba(151,1,251,0.6)]"
              />
            </span>
            <h1 className="font-heading font-extrabold text-white text-3xl sm:text-5xl tracking-tight">
              About <span className="gg-gradient-text">Game Goblin</span>
            </h1>
            <p className="text-white/70 text-base sm:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
              Your companion in the hunt for new games, a proper search engine with a
              recommendation system that actually surprises you.
            </p>
          </div>
        </div>

        {/* Mission */}
        <div className="rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md p-6 sm:p-10 mb-6 animate-fadeUp">
          <h2 className="flex items-center gap-3 font-heading font-extrabold text-2xl sm:text-3xl text-white mb-5">
            <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
            Our story
          </h2>
          <div className="space-y-4 text-white/75 text-base sm:text-lg leading-relaxed font-body">
            <p>
              We've all been there. The endless search for something new to play, without
              quite knowing what game we're looking for. Sometimes you know exactly the type
              of game you want, and other times you just know what you really enjoyed before
              and wish you could find something similar.
            </p>
            <p>
              That's why we created Game Goblin: to help players discover exciting new games
              and get recommendations based on the ones they already love. We felt there was
              a real lack of a proper game search engine combined with a strong
              recommendation system (one that doesn't just keep giving you the same 4–5 titles
              over and over). So we built Game Goblin: your companion in the hunt for new
              games.
            </p>
            <p>
              Describe the kind of game you're looking for in free text, jump straight to a
              game page for one you like and get similar suggestions, use advanced filters, or
              log in and create your own top lists based on your personal criteria.
            </p>
            <p className="text-white/90 font-medium">
              We hope Game Goblin helps you find exactly what you're searching for!
            </p>
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-6">
          {features.map((item, idx) => (
            <div
              key={item.title}
              style={{ animationDelay: `${idx * 70}ms` }}
              className="group rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md p-6 animate-fadeUp transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
            >
              <span
                className="flex items-center justify-center h-14 w-14 rounded-2xl text-2xl mb-4 border"
                style={{ borderColor: `${item.color}55`, background: `${item.color}1f` }}
              >
                {item.icon}
              </span>
              <h3 className="font-heading font-bold text-lg text-white mb-1.5">{item.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md p-6 sm:p-10 mb-6 animate-fadeUp">
          <h2 className="flex items-center gap-3 font-heading font-extrabold text-2xl sm:text-3xl text-white mb-6">
            <span className="h-7 w-1.5 rounded-full bg-gold-gradient shadow-glow-gold" />
            How it works
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {steps.map((s) => (
              <div key={s.n} className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <span className="font-heading font-extrabold text-3xl gg-gradient-text">{s.n}</span>
                <h4 className="font-heading font-bold text-white mt-2 mb-1">{s.title}</h4>
                <p className="text-white/55 text-sm">{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-brand-gradient-135 p-8 sm:p-10 text-center animate-fadeUp">
          <div className="absolute inset-0 gg-grid-backdrop opacity-30" />
          <div className="relative">
            <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-white">
              Ready to find your next favourite game?
            </h3>
            <p className="text-white/80 mt-2 mb-6">Start searching. It's free.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="rounded-full bg-white text-[#0b0916] px-6 py-3 font-heading font-bold shadow-card transition-transform duration-200 hover:-translate-y-0.5"
              >
                Find games
              </Link>
              <Link
                to="/games"
                className="rounded-full border border-white/60 text-white px-6 py-3 font-heading font-bold transition-colors duration-200 hover:bg-white/10"
              >
                Browse games
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
